import React, { useEffect, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
  CircularProgress,
  Paper,
  Typography,
  Tooltip
} from '@material-ui/core'
import { ExternalLink, Check, XCircle, AlertCircle, RefreshCw, FileText, Copy } from 'react-feather'
import { useParams } from 'react-router-dom'
import { usePaymentStore } from '../../stores/payment'
import { useAuthStore } from '../../stores/auth'

export const PaymentsView: React.FC = () => {
  const { id: patronageId } = useParams<{ id: string }>()
  const { payments, loading, fetchPayments } = usePaymentStore()
  const { user } = useAuthStore()
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Check if user is super_admin
  const isSuperAdmin = user?.role === 'super_admin'

  useEffect(() => {
    if (patronageId) {
      fetchPayments(patronageId)
    }
  }, [patronageId, fetchPayments])

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case 'successful':
        return {
          color: '#10B981',
          bgColor: 'rgba(16, 185, 129, 0.1)',
          label: 'Successful',
          icon: Check
        }
      case 'failed':
        return {
          color: '#EF4444',
          bgColor: 'rgba(239, 68, 68, 0.1)',
          label: 'Failed',
          icon: XCircle
        }
      case 'refunded':
        return {
          color: '#F59E0B',
          bgColor: 'rgba(245, 158, 11, 0.1)',
          label: 'Refunded',
          icon: AlertCircle
        }
      case 'processing':
        return {
          color: '#3B82F6',
          bgColor: 'rgba(59, 130, 246, 0.1)',
          label: 'Processing',
          icon: RefreshCw
        }
      case 'draft':
        return {
          color: '#6B7280',
          bgColor: 'rgba(107, 114, 128, 0.1)',
          label: 'Draft',
          icon: FileText
        }
      default:
        return {
          color: '#6B7280',
          bgColor: 'rgba(107, 114, 128, 0.1)',
          label: 'Unknown',
          icon: AlertCircle
        }
    }
  }

  const getStripeUrl = (chargeId: string) => {
    if (!chargeId) return '#'
    const isTest = chargeId.includes('_test_')
    return `https://dashboard.stripe.com/${isTest ? 'test/' : ''}payments/${chargeId}`
  }

  const copyToClipboard = (value: string) => {
    if (!value) return
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(() => {
        setCopiedId(value)
        setTimeout(() => setCopiedId(null), 1500)
      }).catch(() => { })
    }
  }

  if (loading) return <CircularProgress />

  return (
    <TableContainer component={Paper}>
      <Table sx={{ '& td': { borderBottom: 'none' } }}>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Payment Method</TableCell>
            <TableCell>Invoice</TableCell>
            {isSuperAdmin && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {payments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={isSuperAdmin ? 6 : 5} align="center">No payments found</TableCell>
            </TableRow>
          ) : (
            payments.map((payment) => {
              const statusConfig = getStatusConfig(payment.status)
              const StatusIcon = statusConfig.icon
              const invoiceValue = payment.invoiceId || ''
              let invoiceLabel = '—'
              if (invoiceValue) {
                const last = invoiceValue.slice(-8)
                invoiceLabel = invoiceValue.length > 8 ? `…${last}` : invoiceValue
              }

              return (
                <TableRow key={payment.id}>
                  <TableCell>{formatDate(payment.createdAt)}</TableCell>
                  <TableCell>{formatCurrency(payment.amount)}</TableCell>
                  <TableCell>
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        borderRadius: '12px',
                        border: `1px solid ${statusConfig.color}`,
                        backgroundColor: statusConfig.bgColor,
                        width: 'fit-content'
                      }}
                    >
                      <StatusIcon size={12} color={statusConfig.color} />
                      <Typography
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          letterSpacing: '0.025em',
                          color: statusConfig.color
                        }}
                      >
                        {statusConfig.label}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {payment.cardBrand ? `${payment.cardBrand} •••• ${payment.cardLast4 || ''}` : '—'}
                  </TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{invoiceLabel}</span>
                    {invoiceValue && (
                      <Tooltip title={copiedId === invoiceValue ? 'Copied!' : 'Copy invoice ID'}>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(invoiceValue)}
                        >
                          <Copy size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell align="right">
                      {payment.stripeChargeId && (
                        <IconButton
                          size="small"
                          href={getStripeUrl(payment.stripeChargeId)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink size={16} />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
